import React from "react";
import { Card, Row, Col, Statistic, Table, Tag } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  GlobalOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";

const COLORS = {
  countries: ["#1890FF", "#52C41A", "#722ED1", "#FA8C16", "#13C2C2"],
  regions: ["#EB2F96", "#2F54EB", "#FADB14", "#F5222D", "#A0D911"],
};

const GeoAnalytics = ({ geoData }) => {
  if (!geoData) return null;

  // Transform data for charts
  const countryData = Object.entries(geoData.countries || {})
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const regionData = Object.entries(geoData.regions || {})
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const cityData = Object.entries(geoData.cities || {})
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10); // Top 10 cities

  // Calculate engagement by hour
  const timezoneData = Object.entries(geoData.timezones || {})
    .map(([hour, count]) => ({
      hour: parseInt(hour),
      count,
    }))
    .sort((a, b) => a.hour - b.hour);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: "white",
            padding: "12px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>
            {payload[0].payload.name}
          </p>
          <p style={{ margin: "4px 0 0" }}>
            Engagements: {payload[0].value}
            <br />
            Percentage:{" "}
            {(
              (payload[0].value / geoData.metrics.totalLocations) *
              100
            ).toFixed(1)}
            %
          </p>
        </div>
      );
    }
    return null;
  };

  const cityColumns = [
    {
      title: "City",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Engagements",
      dataIndex: "value",
      key: "value",
      render: (value) => <Tag color="blue">{value.toLocaleString()}</Tag>,
    },
    {
      title: "Percentage",
      key: "percentage",
      render: (_, record) => (
        <Tag color="cyan">
          {((record.value / geoData.metrics.totalLocations) * 100).toFixed(1)}%
        </Tag>
      ),
    },
  ];

  return (
    <Row gutter={[24, 24]}>
      {/* Summary Statistics */}
      <Col xs={24} xl={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card bordered={false}>
            <Statistic
              title="Total Countries"
              value={Object.keys(geoData.countries || {}).length}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
            {geoData.geoPatterns?.international && (
              <Tag color="blue" style={{ marginTop: 16 }}>
                International Campaign
              </Tag>
            )}
          </Card>
        </motion.div>
      </Col>
      <Col xs={24} xl={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card bordered={false}>
            <Statistic
              title="Total Regions"
              value={geoData.geoPatterns?.domesticRegions || 0}
              prefix={<EnvironmentOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </motion.div>
      </Col>
      <Col xs={24} xl={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card bordered={false}>
            <Statistic
              title="City Concentration"
              value={
                geoData.geoPatterns?.cityConcentration?.topCitiesPercentage || 0
              }
              prefix={<ClockCircleOutlined />}
              suffix="%"
              precision={1}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </motion.div>
      </Col>

      {/* Country Distribution */}
      <Col xs={24} lg={12}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card title="Country Distribution" bordered={false}>
            <div style={{ height: "400px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={countryData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(1)}%)`
                    }
                  >
                    {countryData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS.countries[index % COLORS.countries.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </Col>

      {/* Engagement by Time */}
      <Col xs={24} lg={12}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card title="Engagement by Hour (UTC)" bordered={false}>
            <div style={{ height: "400px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timezoneData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="hour"
                    tickFormatter={(hour) => `${hour}:00`}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(hour) => `${hour}:00 UTC`}
                    formatter={(value) => [value, "Engagements"]}
                  />
                  <Bar dataKey="count" fill="#722ED1">
                    {timezoneData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={`#722ED1${Math.floor(
                          (entry.count /
                            Math.max(...timezoneData.map((d) => d.count))) *
                            99
                        )
                          .toString(16)
                          .padStart(2, "0")}`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </Col>

      {/* Top Cities Table */}
      <Col xs={24}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Card
            title="Top Cities"
            bordered={false}
            extra={
              <Tag color="purple">
                Total Cities:{" "}
                {geoData.geoPatterns?.cityConcentration?.totalCities || 0}
              </Tag>
            }
          >
            <Table
              columns={cityColumns}
              dataSource={cityData}
              pagination={false}
              rowKey="name"
              size="middle"
            />
          </Card>
        </motion.div>
      </Col>

      {/* Mobility Patterns */}
      <Col xs={24}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Card title="User Mobility Patterns" bordered={false}>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <Statistic
                  title="Stationary Users"
                  value={geoData.geoPatterns?.mobilityPatterns?.stationary || 0}
                  suffix="users"
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="Mobile Users"
                  value={geoData.geoPatterns?.mobilityPatterns?.mobile || 0}
                  suffix="users"
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="Mobility Rate"
                  value={(
                    ((geoData.geoPatterns?.mobilityPatterns?.mobile || 0) /
                      (geoData.geoPatterns?.mobilityPatterns?.stationary +
                        geoData.geoPatterns?.mobilityPatterns?.mobile || 1)) *
                    100
                  ).toFixed(1)}
                  suffix="%"
                />
              </Col>
            </Row>
          </Card>
        </motion.div>
      </Col>
    </Row>
  );
};

export default GeoAnalytics;
